import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import {
  getIsAvailableOpportunity,
  getIsMultipleAvailableOpportunity,
  getIsSingleAvailableOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';
import useIsMultipleStartingPriceOpportunity from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useIsMultipleStartingPriceOpportunity';
import useOpportunityOddsChanges from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useOpportunityOddsChanges';
import getIsDefaultStartingPriceSelected from '@swf/layouts/SportsLayout/components/BetSlip/utils/getIsDefaultStartingPriceSelected';

import { useNumberFromIntl } from '../../../../../../hooks/useNumberFromIntl';
import { calculatePotentialReturns } from '../../../../../../utils/calculatePotentialReturns';

import { TXTWCL_Potential_ReturnsX_N0, TXTWCL_n_a } from './PotentialReturn.messages.json';
import styles from './PotentialReturn.scss';

interface Props {
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const PotentialReturn: React.FC<Props> = ({ opportunity }) => {
  const { input } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const hasAutoAcceptEnabled = useSelector(select.account.hasAutoAcceptEnabled);
  const getNumberFromIntl = useNumberFromIntl();
  const { haveOddsChanged } = useOpportunityOddsChanges(opportunity);
  const isMultipleStartingPriceOpportunity = useIsMultipleStartingPriceOpportunity(opportunity);

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isMultipleAvailableOpportunity = getIsMultipleAvailableOpportunity(opportunity);
  const isDefaultStartingPriceSelected = getIsDefaultStartingPriceSelected(opportunity);
  const {
    eachWaySelected,
    stakePerLineParsed = 0,
    startingPriceSelected = isDefaultStartingPriceSelected,
    freeBet
  } = input.value || {};
  const isFreeBet = !!freeBet?.length;
  const stakePerLine = getNumberFromIntl(stakePerLineParsed);
  const winPotentialReturn = calculatePotentialReturns({
    stake: stakePerLine,
    returnFactor: isAvailableOpportunity ? opportunity.returnFactor ?? 0 : 0,
    numberOfLines: isAvailableOpportunity ? opportunity.numberOfLines : 0,
    isFreeBet
  });
  const placePotentialReturn = eachWaySelected
    ? calculatePotentialReturns({
        stake: stakePerLine,
        returnFactor: isAvailableOpportunity ? opportunity.placeReturnFactor ?? 0 : 0,
        numberOfLines: isAvailableOpportunity ? opportunity.numberOfLines : 0,
        isFreeBet
      })
    : 0;
  const allPotentialReturn = winPotentialReturn + placePotentialReturn;

  let shouldDisplayPotentialReturn = true;
  if (isSingleAvailableOpportunity) {
    shouldDisplayPotentialReturn = Boolean(!startingPriceSelected && opportunity.winRunnerOdds);
  } else if (isMultipleAvailableOpportunity) {
    shouldDisplayPotentialReturn = !isMultipleStartingPriceOpportunity;
  }

  return (
    <p className={styles.returns}>
      <Translation
        {...TXTWCL_Potential_ReturnsX_N0}
        values={{
          0: shouldDisplayPotentialReturn ? (
            <span
              className={classNames(
                haveOddsChanged && !hasAutoAcceptEnabled && allPotentialReturn > 0
                  ? styles.returnValueOddsChanged
                  : styles.returnValue
              )}
            >
              <CurrencyAmount value={allPotentialReturn} />
            </span>
          ) : (
            <Translation {...TXTWCL_n_a} />
          )
        }}
      />
    </p>
  );
};

export default PotentialReturn;
